import React, { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  widgetId: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMsg: string | null;
}

/**
 * Envoltura obligatoria (BMAD6) para atrapar fallos en componentes hijo (gráficos).
 * Garantiza que si un widget falla al procesar datos o renderizar, el dashboard 
 * completo no se crashee.
 */
export class WidgetErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMsg: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMsg: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Aquí se conectaría con un servicio de logging externo (Sentry, Datadog)
    console.error(`Error crítico atrapado en el widget [${this.props.widgetId}]:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full p-4 bg-red-50 text-red-600 rounded-lg">
          <svg className="w-8 h-8 mb-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="font-semibold text-sm">Error al cargar widget</span>
          <span className="text-xs text-center opacity-80 mt-1 line-clamp-2">
            {this.state.errorMsg || 'Datos corruptos o endpoint fallido'}
          </span>
          <button 
            className="mt-3 text-xs bg-red-100 px-3 py-1 rounded hover:bg-red-200 transition-colors"
            onClick={() => this.setState({ hasError: false, errorMsg: null })}
          >
            Reintentar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
